import {SourceTextLayer} from '@waka/compat/SourceTextLayer';
import React, { useContext, useMemo } from 'react';
import { useCurrentFrame, useVideoConfig } from 'remotion';
import {
  TextItemEditingContext,
  TextItemHoverPreviewContext,
} from '../../context-provider';
import { FontInfoContext } from '../../utils/text/font-info';
import {
  loadFontFromTextItem,
  useLoadFontFromTextItem,
} from '../../utils/text/load-font-from-text-item';
import { resolveFontFamilyCssStack } from '../../utils/text/font-family-css-stack';
import { turnFontStyleIntoCss } from '../../utils/text/turn-font-style-into-css';
import {
  calculateAnimationsStyle,
  mergeAnimationStyle,
} from '../animation/calculate-animation';
import {
  calculateFadeInOpacity,
  calculateFadeOutOpacity,
} from '../video/calculate-fade';
import { overrideTextItemWithHoverPreview } from './override-text-item-with-hover-preview';
import { getTextEffect } from './effects/registry';
import { RichTextEditor } from './rich-text-editor';
import { TextBackgroundRenderer } from './TextBackgroundRenderer';
import {
  WORD_BREAK_STYLE,
  TextItem,
  TextShadow,
  defaultTextShadow,
  defaultTextItemBackground,
} from './text-item-type';

const NativeTextLayer = ({
  item: itemWithoutHoverPreview,
  zIndex,
}: {
  item: TextItem;
  zIndex?: number;
}) => {
  if (itemWithoutHoverPreview.type !== 'text') {
    throw new Error('Item is not a text');
  }

  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  const textItemHoverPreview = useContext(TextItemHoverPreviewContext);
  const item = useMemo(
    () =>
      overrideTextItemWithHoverPreview({
        textItem: itemWithoutHoverPreview,
        hoverPreview: textItemHoverPreview,
      }),
    [itemWithoutHoverPreview, textItemHoverPreview],
  );

  const shadow = useMemo<TextShadow>(() => {
    return {
      ...defaultTextShadow,
      ...(item.shadow ?? {}),
    };
  }, [item.shadow]);

  // 배경 설정 (visible 체크를 위해)
  const background = useMemo(() => {
    return {
      ...defaultTextItemBackground,
      ...(item.background ?? {}),
    };
  }, [item.background]);
  const effectiveColor =
    item.colorVisible === false ? 'transparent' : item.color;
  const effectiveStrokeColor =
    item.strokeVisible === false ? 'transparent' : item.strokeColor;

  const opacity = useMemo(() => {
    const inOpacity = calculateFadeInOpacity({
      fadeWindow:item.visualFadeWindow,
      currentFrame: frame,
      fadeInDurationInSeconds: item.fadeInDurationInSeconds,
      framesPerSecond: fps,
    });
    const outOpacity = calculateFadeOutOpacity({
      fadeWindow:item.visualFadeWindow,
      currentFrame: frame,
      fadeOutDurationInSeconds: item.fadeOutDurationInSeconds,
      framesPerSecond: fps,
      totalDurationInFrames: item.durationInFrames,
    });
    return inOpacity * outOpacity * item.opacity;
  }, [
    item.visualFadeWindow,
    item.fadeInDurationInSeconds,
    fps,
    frame,
    item.opacity,
    item.durationInFrames,
    item.fadeOutDurationInSeconds,
  ]);

  const context = useContext(FontInfoContext);
  const textItemEditing = useContext(TextItemEditingContext);

  // 캡션과 동일하게 useLoadFontFromTextItem 훅 사용
  // 폰트가 로드될 때까지 렌더링하지 않음 (측정 정확성 보장)
  const fontLoaded = useLoadFontFromTextItem({
    fontFamily: item.fontFamily,
    fontVariant: item.fontStyle.variant,
    fontWeight: item.fontStyle.weight,
    fontInfosDuringRendering: context[item.fontFamily] ?? null,
  });

  // segments 내 개별 폰트도 로드 (Rich Text 부분 스타일 지원)
  if (item.segments) {
    for (const segment of item.segments) {
      if (segment.fontFamily) {
        loadFontFromTextItem({
          fontFamily: segment.fontFamily,
          fontVariant: segment.fontVariant ?? 'normal',
          fontWeight: segment.fontWeight ?? '400',
          fontInfosDuringRendering: context[segment.fontFamily] ?? null,
        });
      }
    }
  }

  // Animation 스타일 계산
  const animationStyle = useMemo(() => {
    const hasAnimations = item.animations && item.animations.length > 0;
    const hasPositionKeyframes =
      item.positionKeyframes && item.positionKeyframes.length > 0;
    if (!hasAnimations && !hasPositionKeyframes) {
      return {};
    }
    return calculateAnimationsStyle({
      animations: item.animations ?? [],
      currentFrame: frame,
      framesPerSecond: fps,
      totalDurationInFrames: item.durationInFrames,
      positionKeyframes: item.positionKeyframes,
    });
  }, [
    item.animations,
    item.positionKeyframes,
    frame,
    fps,
    item.durationInFrames,
  ]);

  // 내용 기반 텍스트 이펙트(타자기 등) — 레지스트리 디스패치.
  // rich text(segments) 경로는 미적용(v1). 없으면 전체 텍스트 그대로.
  const effectRender = useMemo(() => {
    if (!item.effect) return null;
    if (item.segments && item.segments.length > 0) return null;
    const def = getTextEffect(item.effect.type);
    if (!def) return null; // 레지스트리 누락 → 효과 미적용(전체 텍스트)
    return def.compute(item.text, frame, fps, item.effect);
  }, [item.effect, item.segments, item.text, frame, fps]);
  const displayText = effectRender ? effectRender.visibleText : item.text;
  const showEffectCursor = effectRender?.cursor ?? false;

  // segments가 있거나 편집 모드면 RichTextEditor 사용
  const hasSegments = Boolean(item.segments && item.segments.length > 0);
  const isEditing = item.id === textItemEditing;

  // 폰트 로딩 완료 전에는 렌더링하지 않음 (캡션과 동일한 패턴)
  if (!fontLoaded) {
    return null;
  }

  const cssFontFamily = resolveFontFamilyCssStack(item.fontFamily);

  if (isEditing) {
    // 편집 모드: RichTextEditor (editable) + 배경/그림자 유지
    const backgroundZIndex = zIndex !== undefined ? zIndex - 1 : -1;
    return (
      <>
        {background.visible && item.backgroundVisible !== false && (
          <TextBackgroundRenderer
            item={item}
            opacity={opacity}
            zIndex={backgroundZIndex}
          />
        )}
        <RichTextEditor
          item={{
            ...item,
            color: effectiveColor,
            strokeColor: effectiveStrokeColor,
          }}
          zIndex={zIndex}
          editable={true}
        />
      </>
    );
  }

  if (hasSegments) {
    // 일반 모드 + segments 있음: RichTextEditor (readOnly)
    // 동일한 렌더러로 일관된 렌더링 보장
    // key: segments 변경 시 재마운트하여 에디터 상태 동기화
    const segmentsKey = JSON.stringify(item.segments);
    // 배경은 텍스트보다 뒤에 위치 (텍스트 > 쉐도우 > 배경 순서)
    const backgroundZIndex = zIndex !== undefined ? zIndex - 1 : -1;
    // Animation이 있으면 래퍼 스타일 적용
    const wrapperStyle = mergeAnimationStyle(
      {
        position: 'absolute',
        left: item.left,
        top: item.top,
        width: item.width,
        height: item.height,
      },
      animationStyle,
    );
    const hasAnimations = item.animations && item.animations.length > 0;
    return (
      <div style={hasAnimations ? wrapperStyle : undefined}>
        {background.visible && item.backgroundVisible !== false && (
          <TextBackgroundRenderer
            item={item}
            opacity={opacity}
            zIndex={backgroundZIndex}
          />
        )}
        <div style={{ pointerEvents: 'none' }}>
          <RichTextEditor
            key={segmentsKey}
            item={{
              ...item,
              color: effectiveColor,
              strokeColor: effectiveStrokeColor,
              opacity,
              // animations 있으면 래퍼에서 position 처리하므로 상대 위치로 변경
              left: hasAnimations ? 0 : item.left,
              top: hasAnimations ? 0 : item.top,
            }}
            zIndex={zIndex}
            editable={false}
          />
        </div>
      </div>
    );
  }

  // 일반 모드 + segments 없음: 기존 방식
  // 배경은 텍스트보다 뒤에 위치 (텍스트 > 쉐도우 > 배경 순서)
  const backgroundZIndex = zIndex !== undefined ? zIndex - 1 : -1;
  const baseStyle: React.CSSProperties = {
    fontSize: item.fontSize,
    color: effectiveColor,
    lineHeight: String(item.lineHeight),
    letterSpacing: `${item.letterSpacing}px`,
    left: item.left,
    top: item.top,
    width: item.width,
    height: item.height,
    position: 'absolute',
    whiteSpace: item.whiteSpace ?? 'pre-wrap',
    // 자동 줄바꿈(soft wrap) 시 줄 길이를 균형 있게 분배(CSS text-wrap: balance).
    // nowrap(줄바꿈 없음) 아이템에는 적용하지 않아 기존 단일 줄 레이아웃 회귀를 막는다.
    textWrap: item.whiteSpace === 'nowrap' ? undefined : 'balance',
    display: 'block',
    fontFamily: cssFontFamily,
    ...turnFontStyleIntoCss(item.fontStyle),
    overflow: item.overflow ?? 'visible',
    textOverflow: item.textOverflow,
    ...WORD_BREAK_STYLE,
    boxSizing: 'border-box',
    userSelect: 'none',
    textAlign: item.align,
    opacity,
    transform: `rotate(${item.rotation}deg)`,
    pointerEvents: 'auto',
    zIndex,
    isolation: zIndex !== undefined ? 'isolate' : undefined,
  };
  const finalStyle = mergeAnimationStyle(baseStyle, animationStyle);
  return (
    <>
      {background.visible && item.backgroundVisible !== false && (
        <TextBackgroundRenderer
          item={item}
          opacity={opacity}
          zIndex={backgroundZIndex}
        />
      )}
      <div
        dir={item.direction}
        style={finalStyle}
      >
        {shadow.visible && item.shadowVisible !== false && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              left: shadow.offsetX,
              top: shadow.offsetY,
              color: shadow.color,
              opacity: shadow.opacity,
              filter: shadow.blur > 0 ? `blur(${shadow.blur}px)` : undefined,
              WebkitTextStroke: item.strokeWidth
                ? `${item.strokeWidth}px ${shadow.color}`
                : '0',
              pointerEvents: 'none',
              userSelect: 'none',
              display: 'block',
              whiteSpace: 'inherit',
              overflow: 'inherit',
              textOverflow: 'inherit',
              lineHeight: 'inherit',
              letterSpacing: 'inherit',
              width: '100%',
              zIndex: 0,
              fontFamily: cssFontFamily,
              ...turnFontStyleIntoCss(item.fontStyle),
            }}
          >
            {displayText}
            {showEffectCursor && <span aria-hidden>|</span>}
          </span>
        )}
        <span
          style={{
            position: 'relative',
            display: 'block',
            whiteSpace: 'inherit',
            overflow: 'inherit',
            textOverflow: 'inherit',
            zIndex: 1,
            WebkitTextStroke: item.strokeWidth
              ? `${item.strokeWidth}px ${effectiveStrokeColor}`
              : '0',
            paintOrder: 'stroke',
            fontFamily: cssFontFamily,
            ...turnFontStyleIntoCss(item.fontStyle),
          }}
        >
          {displayText}
          {showEffectCursor && (
            <span
              aria-hidden
              style={{ color: effectiveColor }}
            >
              |
            </span>
          )}
        </span>
      </div>
    </>
  );
};

/** React19 routing stays inside native bundle; source label module contains no Remotion/root hooks. */
export const TextLayer=(props:{item:TextItem;zIndex?:number})=>props.item.sourceText?<SourceTextLayer {...props}/>:<NativeTextLayer {...props}/>;
